import React, { ReactNode, useEffect, useMemo } from 'react';

import { Context as MediaQueryContext } from 'react-responsive';

import { usePageCanvas } from '../hooks/usePageCanvas';
import { CANVAS_ATTR, CANVAS_WIDTH_VAR, canvasTokens } from '../utils/pageCanvas';

/**
 * Re-points the app's responsive system at the page canvas instead of the window.
 *
 * react-responsive resolves `useMediaQuery` against this context when it is supplied, so every
 * existing call site starts answering "how much room does the page have" rather than "how wide is
 * the monitor" — with no edit at any of them. At inset 0 the canvas IS the viewport, so nothing
 * about the app changes until the drawer opens.
 *
 * The same measurement is published to CSS twice: `--page-canvas-width` for calc(), and a
 * `data-canvas` token list for the styled-components files, which cannot use container queries
 * (styled-components v5 compiles `@container` to invalid CSS).
 */
export function PageCanvasProvider({ children }: { children: ReactNode }) {
  const canvas = usePageCanvas();
  const width = canvas?.width ?? null;

  useEffect(() => {
    const root = document.documentElement;
    if (width === null) return;

    root.style.setProperty(CANVAS_WIDTH_VAR, `${width}px`);
    const tokens = canvasTokens(width);
    if (tokens) root.setAttribute(CANVAS_ATTR, tokens);
    else root.removeAttribute(CANVAS_ATTR);

    return () => {
      root.style.removeProperty(CANVAS_WIDTH_VAR);
      root.removeAttribute(CANVAS_ATTR);
    };
  }, [width]);

  const device = useMemo(
    // Height is published even though nothing queries it yet: without it, a future
    // `useMediaQuery({ maxHeight })` would silently answer false forever under this provider.
    () => (canvas === null ? null : { width: canvas.width, height: canvas.height }),
    [canvas],
  );

  // Decline rather than publish an unusable width: css-mediaquery answers false to BOTH
  // `max-width` and `min-width` when the value is missing, which collapses every layout at once.
  if (device === null) return <>{children}</>;

  return <MediaQueryContext.Provider value={device}>{children}</MediaQueryContext.Provider>;
}
